import React, { FC, useEffect, useRef, useState } from 'react';
import TwDialog from '../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import { Grid, Typography } from '@mui/material';
import TwField, { TwInputTypes } from '../../components/TwFields/TwFields';
import { useFormik } from 'formik';
import TwCheckbox from '../../components/TwCheckboxes/TwCheckboxes';
import TwSelect from '../../components/TwSelects/TwSelects';
import TwImperialSystemInput from '../../components/TwImperialSystemInput/TwImperialSystemInput';
import {
  convertCmToFeetInches,
  imperialToCm,
  unitOptions,
  validateDimensions
} from '../../utils/commodity/commodityUtils';
import {
  useGetCommodityByIdQuery,
  useCreateCommodityMutation,
  useUpdateCommodityMutation
} from '../../redux/api/commodities/commoditiesApi';
import { CommodityUnit } from '../../redux/api/commodities/commodities.model';
import { MeasurementSystem } from '../../providers/User/User.model';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import TwMetricSystemInput from '../../components/TwMetricSystemInput/TwMetricSystemInput';
import TwBackdrop from '../../components/TwBackdrop/TwBackdrop';
import { validationSchema } from './CommodiyFormModalValidations';
import { CommodityFormValues, CreateCommodityModalProps, DimensionErrors } from './types';
import { useCommoditySettings } from '../../hooks/useCommoditySettings';

const initialValues: CommodityFormValues = {
  name: '',
  unit: null,
  weight: null,
  length_right: null,
  length_left: null,
  width_right: null,
  width_left: null,
  height_right: null,
  height_left: null,
  length: null,
  width: null,
  height: null,
  commodity_id: null,
  permit: false,
  notes: null,
  make: null,
  model: null
};

export const CommodityFormModal: FC<CreateCommodityModalProps> = ({
  open,
  onClose,
  onSuccess,
  commodityId,
  isDuplicateMode
}): JSX.Element => {
  const { addNotification } = useNotifications();
  const [dimensionErrors, setDimensionErrors] = useState<DimensionErrors>({});
  const { data: selectedCommodity, isLoading: isLoadingCommodity } = useGetCommodityByIdQuery(
    commodityId || '',
    { skip: !commodityId }
  );
  const {
    isCommodityIdEnabled,
    isPermitRequirementEnabled,
    isDimensionsEnabled,
    isUnitEnabled,
    isModelEnabled,
    isMakeEnabled,
    measurementSystem
  } = useCommoditySettings();

  const [createCommodity, { isLoading: isCreating }] = useCreateCommodityMutation();
  const [updateCommodity, { isLoading: isUpdating }] = useUpdateCommodityMutation();

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Reset previous errors
        setDimensionErrors({});

        const newErrors: DimensionErrors = validateDimensions(
          values.length,
          values.width,
          values.height,
          measurementSystem
        );

        if (Object.keys(newErrors).length > 0) {
          setDimensionErrors(newErrors);
          return;
        }

        const commodityData = {
          name: values.name,
          unit: values.unit ? (values.unit as CommodityUnit) : null,
          weight: values.weight ? Number(values.weight) : null,
          length:
            values.length_left || values.length_right || values.length
              ? measurementSystem === MeasurementSystem.IMPERIAL
                ? imperialToCm(Number(values.length_left), Number(values.length_right))
                : Number(values.length)
              : null,
          width:
            values.width_left || values.width_right || values.width
              ? measurementSystem === MeasurementSystem.IMPERIAL
                ? imperialToCm(Number(values.width_left), Number(values.width_right))
                : Number(values.width)
              : null,
          height:
            values.height_left || values.height_right || values.height
              ? measurementSystem === MeasurementSystem.IMPERIAL
                ? imperialToCm(Number(values.height_left), Number(values.height_right))
                : Number(values.height)
              : null,
          commodity_id: values.commodity_id,
          requires_permit: values.permit,
          notes: values.notes || null,
          measurement_system: measurementSystem,
          quantity: 1,
          make: values.make || null,
          model: values.model || null
        };

        if (commodityId && !isDuplicateMode) {
          try {
            const result = await updateCommodity({
              id: commodityId,
              body: commodityData
            }).unwrap();

            if (result && result.id) {
              addNotification({
                message: 'Commodity updated successfully',
                variant: 'success',
                timeToShow: 3000
              });
              resetForm();
              onSuccess?.(result);
              onClose();
            }
          } catch (error) {
            const err = error as { data?: { message?: string }; status?: number };
            addNotification({
              message: err.data?.message || 'Failed to update commodity',
              variant: 'error',
              timeToShow: 3000
            });
          }
        } else {
          try {
            const result = await createCommodity(commodityData).unwrap();

            if (result && result.id) {
              addNotification({
                message: 'Commodity created successfully',
                variant: 'success',
                timeToShow: 3000
              });
              resetForm();
              onSuccess?.(result);
              onClose();
            }
          } catch (error) {
            const err = error as { data?: { message?: string }; status?: number };
            addNotification({
              message: err.data?.message || 'Failed to create commodity',
              variant: 'error',
              timeToShow: 3000
            });
          }
        }
      } catch (error) {
        addNotification({
          message: commodityId ? 'Failed to update commodity' : 'Failed to create commodity',
          variant: 'error',
          timeToShow: 3000
        });
      }
    }
  });

  const { values, handleChange, handleSubmit, errors, touched, setFieldValue, setValues } = formik;

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (selectedCommodity) {
      const commodityFormValues: CommodityFormValues = {
        name: isDuplicateMode ? `${selectedCommodity.name} (Copy)` : selectedCommodity.name,
        unit: selectedCommodity.unit || null,
        weight: selectedCommodity.weight?.toString() || '',
        commodity_id: selectedCommodity.commodity_id || '',
        permit: selectedCommodity.requires_permit,
        notes: selectedCommodity.notes || '',
        length: selectedCommodity.length?.toString() || null,
        width: selectedCommodity.width?.toString() || null,
        height: selectedCommodity.height?.toString() || null,
        length_right: '',
        length_left: '',
        width_right: '',
        width_left: '',
        height_right: '',
        height_left: '',
        make: selectedCommodity.make || '',
        model: selectedCommodity.model || '',
        is_linked: selectedCommodity.is_linked || false
      };
      if (measurementSystem === MeasurementSystem.IMPERIAL) {
        // Convert cm measurements to feet and inches, or set empty strings if null
        const length = selectedCommodity.length
          ? convertCmToFeetInches(selectedCommodity.length.toString())
          : { feet: '', inches: '' };
        const width = selectedCommodity.width
          ? convertCmToFeetInches(selectedCommodity.width.toString())
          : { feet: '', inches: '' };
        const height = selectedCommodity.height
          ? convertCmToFeetInches(selectedCommodity.height.toString())
          : { feet: '', inches: '' };
        commodityFormValues.height_left = height.feet;
        commodityFormValues.height_right = height.inches;
        commodityFormValues.width_left = width.feet;
        commodityFormValues.width_right = width.inches;
        commodityFormValues.length_left = length.feet;
        commodityFormValues.length_right = length.inches;
      }

      setValues(commodityFormValues);
    }
  }, [selectedCommodity, isDuplicateMode, setValues, measurementSystem]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setDimensionErrors({});
    }
  }, [open]);

  return (
    <TwDialog
      open={open}
      onClickClose={onClose}
      title={
        isDuplicateMode ? 'Duplicate commodity' : commodityId ? 'Edit commodity' : 'New commodity'
      }
      actions={[
        <TwButton key="1" type={TwButtonType.secondary} label="Cancel" action={onClose} />,
        <TwButton
          key="2"
          type={TwButtonType.primary}
          label={commodityId ? 'Save' : 'Create'}
          disabled={isCreating || isUpdating}
          action={(): void => {
            formRef.current?.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true })
            );
          }}
        />
      ]}
    >
      {isLoadingCommodity ? (
        <TwBackdrop isOpen={true} />
      ) : (
        <form
          ref={formRef}
          onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
            e.preventDefault();
            handleSubmit(e);
          }}
          noValidate
        >
          {/* First line: Name */}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TwField
                id="name"
                name="name"
                label="Name*"
                value={values.name}
                onInputChange={handleChange}
                inputType={TwInputTypes.text}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name ? errors.name : undefined}
                fullWidth
                disabled={(values.is_linked && !isDuplicateMode) || false}
              />
            </Grid>
          </Grid>

          {/* Second line: Unit, Commodity ID */}
          {(isUnitEnabled || isCommodityIdEnabled) && (
              <Grid container spacing={2} sx={{ height: '95px' }}>
                {isUnitEnabled && (
                  <Grid item xs={isCommodityIdEnabled ? 6 : 12}>
                    <TwSelect
                      id="unit"
                      name="unit"
                      label="Unit"
                      value={values.unit || ''}
                      onInputChange={(e): void => {
                        void setFieldValue('unit', e.target.value);
                      }}
                      optionList={unitOptions}
                    />
                  </Grid>
                )}

                {isCommodityIdEnabled && (
                  <Grid item xs={isUnitEnabled ? 6 : 12}>
                    <TwField
                      id="commodity_id"
                      name="commodity_id"
                      label="Commodity ID"
                      value={values.commodity_id || ''}
                      onInputChange={handleChange}
                      error={Boolean(touched.commodity_id && errors.commodity_id)}
                      helperText={touched.commodity_id ? errors.commodity_id : undefined}
                      disabled={(values.is_linked && !isDuplicateMode) || false}
                    />
                  </Grid>
                )}
              </Grid>
            )}

          {/* Third line: Dimensions, Weight */}
          <Grid container spacing={2}>
            {isDimensionsEnabled && (
              <>
                <Grid item xs={3}>
                  {measurementSystem === MeasurementSystem.IMPERIAL ? (
                    <TwImperialSystemInput
                      leftValue={values.length_left || ''}
                      rightValue={values.length_right || ''}
                      onChange={(value): void => {
                        setFieldValue('length_right', value.rightValue);
                        setFieldValue('length_left', value.leftValue);
                      }}
                      type="length"
                      measurementSystem={measurementSystem}
                      externalError={dimensionErrors.length}
                      label="Length"
                    />
                  ) : (
                    <TwMetricSystemInput
                      cmValue={values.length}
                      onChange={(value): void => {
                        setFieldValue('length', value);
                      }}
                      type="length"
                      label="Length"
                      externalError={dimensionErrors.length}
                    />
                  )}
                </Grid>

                <Grid item xs={3}>
                  {measurementSystem === MeasurementSystem.IMPERIAL ? (
                    <TwImperialSystemInput
                      leftValue={values.width_left || ''}
                      rightValue={values.width_right || ''}
                      onChange={(value): void => {
                        setFieldValue('width_right', value.rightValue);
                        setFieldValue('width_left', value.leftValue);
                      }}
                      type="width"
                      measurementSystem={measurementSystem}
                      externalError={dimensionErrors.width}
                      label="Width"
                    />
                  ) : (
                    <TwMetricSystemInput
                      cmValue={values.width}
                      onChange={(value): void => {
                        setFieldValue('width', value);
                      }}
                      type="width"
                      label="Width"
                      externalError={dimensionErrors.width}
                    />
                  )}
                </Grid>

                <Grid item xs={3}>
                  {measurementSystem === MeasurementSystem.IMPERIAL ? (
                    <TwImperialSystemInput
                      leftValue={values.height_left || ''}
                      rightValue={values.height_right || ''}
                      onChange={(value): void => {
                        setFieldValue('height_right', value.rightValue);
                        setFieldValue('height_left', value.leftValue);
                      }}
                      type="height"
                      measurementSystem={measurementSystem}
                      externalError={dimensionErrors.height}
                      label="Height"
                    />
                  ) : (
                    <TwMetricSystemInput
                      cmValue={values.height}
                      onChange={(value): void => {
                        setFieldValue('height', value);
                      }}
                      type="height"
                      label="Height"
                      externalError={dimensionErrors.height}
                    />
                  )}
                </Grid>
              </>
            )}
            <Grid item xs={isDimensionsEnabled ? 3 : 6} sx={{ paddingTop: measurementSystem === MeasurementSystem.IMPERIAL ? '11px !important' : '16px !important' }}>
              <TwField
                id="weight"
                name="weight"
                label="Weight"
                value={values.weight || ''}
                onInputChange={handleChange}
                inputType={TwInputTypes.number}
                endAdorment={
                  measurementSystem === MeasurementSystem.IMPERIAL ? (
                    <Typography color="#9CA3AF" sx={{ fontSize: '14px' }}>
                      lb
                    </Typography>
                  ) : (
                    <Typography color="#9CA3AF" sx={{ fontSize: '14px' }}>
                      kg
                    </Typography>
                  )
                }
              />
            </Grid>
          </Grid>

          {/* Fourth line: Make, Model */}
          {(isMakeEnabled || isModelEnabled) && (
              <Grid container spacing={2}>
                {isMakeEnabled && (
                  <Grid item xs={6}>
                    <TwField
                      id="make"
                      name="make"
                      label="Make"
                      value={values.make || ''}
                      onInputChange={handleChange}
                      error={Boolean(touched.make && errors.make)}
                      helperText={touched.make ? errors.make : undefined}
                    />
                  </Grid>
                )}

                {isModelEnabled && (
                  <Grid item xs={6}>
                    <TwField
                      id="model"
                      name="model"
                      label="Model"
                      value={values.model || ''}
                      onInputChange={handleChange}
                      error={Boolean(touched.model && errors.model)}
                      helperText={touched.model ? errors.model : undefined}
                    />
                  </Grid>
                )}
              </Grid>
            )}

          {/* Fifth line: Permit required */}
          {isPermitRequirementEnabled && (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TwCheckbox
                  name="permit"
                  label="Requires permit"
                  checked={values.permit}
                  onInputChange={handleChange}
                />
              </Grid>
            </Grid>
          )}

          {/* Sixth line: Notes */}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TwField
                id="notes"
                name="notes"
                label="Notes"
                value={values.notes || ''}
                onInputChange={handleChange}
                inputType={TwInputTypes.textareaAuto}
                maxRows={3}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </TwDialog>
  );
};

export default CommodityFormModal;
